import React, { useMemo, useState } from 'react';
import { Button, Grid, Select } from 'antd';
import { LayoutGrid, List, Plus, SlidersHorizontal } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { BarcodeSearchInput } from '@/components/BarcodeSearchInput';
import { useGetInfinityCategory } from '@/store/category/useGetInfinityCategories';
import { handleInfiniteScroll } from '@/components/ModalAddPlanogramFromProduct/_components/FormPlanogram/utils';
import {
    getPropertyNamesFromProductStatus,
    ProductStatus,
} from '@/interfaces/Planogram';
import { SortOption, StatusFilterValue, ViewMode } from '../../types';
import { getSearchTerm } from '../../utils';
import * as S from './styles';

const ALL_CATEGORIES = 'all';

const { Option } = Select;

const STATUS_OPTIONS: { value: StatusFilterValue; label: string }[] = [
    { value: 'all', label: 'Todos os status' },
    ...getPropertyNamesFromProductStatus().map((key) => ({
        value: key as StatusFilterValue,
        label: ProductStatus[key as keyof typeof ProductStatus],
    })),
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'category', label: 'Ordenar: Categoria' },
    { value: 'due_date', label: 'Ordenar: Vencimento (mais próximo)' },
    { value: 'stock', label: 'Ordenar: Estoque (menor folga)' },
];

type SearchAndFiltersProps = {
    search: string;
    onSearchChange: (value: string) => void;
    categoryId: number | undefined;
    onCategoryChange: (categoryId: number | undefined) => void;
    status: StatusFilterValue;
    onStatusChange: (status: StatusFilterValue) => void;
    sort: SortOption;
    onSortChange: (sort: SortOption) => void;
    viewMode: ViewMode;
    onViewModeChange: (viewMode: ViewMode) => void;
    resultLabel: string;
    onAddProduct: () => void;
};

export const SearchAndFilters = ({
    search,
    onSearchChange,
    categoryId,
    onCategoryChange,
    status,
    onStatusChange,
    sort,
    onSortChange,
    viewMode,
    onViewModeChange,
    resultLabel,
    onAddProduct,
}: SearchAndFiltersProps) => {
    const { lg } = Grid.useBreakpoint();
    const isMobile = !lg;
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    const [categorySearch, setCategorySearch] = useState('');
    const debouncedCategorySearch = useDebounce(categorySearch, 400);
    const {
        data: categoriesPaginated,
        hasNextPage,
        fetchNextPage,
        isFetching,
    } = useGetInfinityCategory({
        name: getSearchTerm(debouncedCategorySearch),
    });

    const categoryOptions = useMemo(
        () => categoriesPaginated?.pages.flatMap((page) => page.data) ?? [],
        [categoriesPaginated],
    );

    const renderFiltersContent = (equalWidth: boolean) => (
        <S.FiltersRow $equalWidth={equalWidth}>
            <S.FilterSelect
                popupMatchSelectWidth={false}
                placeholder="Categoria"
                showSearch
                value={categoryId ?? ALL_CATEGORIES}
                onChange={(value) =>
                    onCategoryChange(
                        value === ALL_CATEGORIES
                            ? undefined
                            : (value as number),
                    )
                }
                onSearch={setCategorySearch}
                filterOption={false}
                onPopupScroll={(e) =>
                    handleInfiniteScroll({
                        e,
                        hasNextPage,
                        isFetching,
                        fetchNextPage,
                    })
                }
            >
                <Option value={ALL_CATEGORIES}>Todas as categorias</Option>
                {categoryOptions.map((category) => (
                    <Option key={category.id} value={Number(category.id)}>
                        {category.name}
                    </Option>
                ))}
            </S.FilterSelect>

            <S.FilterSelect
                popupMatchSelectWidth={false}
                placeholder="Status"
                value={status}
                onChange={(value) => onStatusChange(value as StatusFilterValue)}
            >
                {STATUS_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </S.FilterSelect>

            <S.FilterSelect
                popupMatchSelectWidth={false}
                placeholder="Ordenar por"
                value={sort}
                onChange={(value) => onSortChange(value as SortOption)}
            >
                {SORT_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                        {option.label}
                    </Option>
                ))}
            </S.FilterSelect>
        </S.FiltersRow>
    );

    return (
        <>
            <S.Container>
                <S.TopRow>
                    <BarcodeSearchInput
                        value={search}
                        onChange={onSearchChange}
                    />

                    {isMobile && (
                        <Button
                            icon={<SlidersHorizontal size={16} />}
                            onClick={() =>
                                setShowMobileFilters((prev) => !prev)
                            }
                        >
                            Filtros
                        </Button>
                    )}

                    {!isMobile && renderFiltersContent(false)}

                    <S.AddButton
                        type="primary"
                        icon={<Plus size={16} />}
                        onClick={onAddProduct}
                    >
                        Adicionar produto
                    </S.AddButton>
                </S.TopRow>

                {isMobile && showMobileFilters && renderFiltersContent(true)}
            </S.Container>

            <S.ResultLine>
                <span>{resultLabel}</span>
                <S.ResultLineActions>
                    {search && (
                        <Button type="link" onClick={() => onSearchChange('')}>
                            Limpar busca
                        </Button>
                    )}
                    <S.ViewToggle>
                        <S.ViewToggleButton
                            type="button"
                            title="Ver em cards"
                            active={viewMode === 'cards'}
                            onClick={() => onViewModeChange('cards')}
                        >
                            <LayoutGrid size={15} />
                        </S.ViewToggleButton>
                        <S.ViewToggleButton
                            type="button"
                            title="Ver em lista"
                            active={viewMode === 'list'}
                            onClick={() => onViewModeChange('list')}
                        >
                            <List size={15} />
                        </S.ViewToggleButton>
                    </S.ViewToggle>
                </S.ResultLineActions>
            </S.ResultLine>
        </>
    );
};

export default SearchAndFilters;
